/**
 * Sourcing API - Client for AI-powered candidate sourcing pipeline
 */

import { axiosInstance as api } from "./api";

export interface SourcingRun {
  id: number;
  job_id: number;
  phase: "internal" | "external" | "semantic";
  source: "ai_finder" | "apollo" | "semantic_search";
  status: "pending" | "running" | "completed" | "failed";
  results_count: number;
  search_criteria: string | null;
  error_message: string | null;
  created_by: number;
  created_by_name?: string;
  created_at: string;
  completed_at: string | null;
}

export interface SourcingMatch {
  id: number;
  run_id: number;
  candidate_id: number | null;
  external_profile_id: string | null;
  match_score: number | null;
  rating: string | null;
  strengths: string[];
  gaps: string[];
  recommendation: string | null;
  action_taken: string | null;
  external_data: ExternalProfileData | null;
  created_at: string;
  phase: string;
  source: string;
  // Joined candidate fields
  first_name?: string;
  last_name?: string;
  candidate_email?: string;
  main_skill?: string;
  seniority?: string;
  candidate_location?: string;
}

export interface ExternalProfileData {
  apolloId?: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string | null;
  emailStatus: string | null;
  linkedinUrl: string | null;
  title: string;
  company: string;
  location: string;
  skills: string[];
  summary: string;
  profilePictureUrl: string | null;
  apolloData?: {
    headline: string;
    experience: unknown[];
    education: unknown[];
    languages: string[];
  };
}

export interface RunOptions {
  country?: string;
  perPage?: number;
  query?: string;
  topK?: number;
}

export interface UnlockedContact {
  id: number;
  apollo_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  email_source: string | null;
  email_validated: boolean;
  linkedin_url: string | null;
  phone: string | null;
  title: string;
  company: string;
  location: string;
  unlock_type: "unlock" | "deepsearch";
  deepsearch_status: "success" | "unsuccessful" | null;
  credits_consumed: number;
  created_at: string;
}

export interface DeepsearchResponse {
  success: boolean;
  data: UnlockedContact;
  derivedEmail: string | null;
  emailValid: boolean;
  status: "success" | "unsuccessful";
  cached: boolean;
}

export interface CreditUsage {
  used: number;
  limit: number;
  remaining: number;
  resetsAt: string;
}

export const sourcingAPI = {
  // Trigger a sourcing run
  triggerRun: async (
    jobId: number | string,
    phase: string,
    source: string,
    options?: RunOptions,
  ) => {
    const response = await api.post(`/sourcing/jobs/${jobId}/run`, {
      phase,
      source,
      options,
    });
    return response.data;
  },

  // Get sourcing runs for a job
  getRuns: async (jobId: number | string) => {
    const response = await api.get(`/sourcing/jobs/${jobId}/runs`);
    return response.data;
  },

  // Get matches for a job
  getMatches: async (
    jobId: number | string,
    filters?: { phase?: string; minScore?: number; runId?: number },
  ) => {
    const params = new URLSearchParams();
    if (filters?.phase) params.set("phase", filters.phase);
    if (filters?.minScore) params.set("minScore", String(filters.minScore));
    if (filters?.runId) params.set("runId", String(filters.runId));
    const response = await api.get(
      `/sourcing/jobs/${jobId}/matches?${params.toString()}`,
    );
    return response.data;
  },

  // Update match action
  updateMatchAction: async (matchId: number, action: string) => {
    const response = await api.patch(`/sourcing/matches/${matchId}/action`, {
      action,
    });
    return response.data;
  },

  // Create application from match
  applyFromMatch: async (matchId: number) => {
    const response = await api.post(`/sourcing/matches/${matchId}/apply`);
    return response.data;
  },

  // Bulk apply from matches
  bulkApply: async (matchIds: number[]) => {
    const response = await api.post("/sourcing/matches/bulk-apply", {
      matchIds,
    });
    return response.data;
  },

  // Unlock external contact (1 Apollo credit)
  unlockContact: async (apolloId: string) => {
    const response = await api.post(`/sourcing/unlock/${apolloId}`);
    return response.data;
  },

  // Deepsearch external contact (1-2 Apollo credits + NeverBounce)
  deepsearchContact: async (apolloId: string): Promise<DeepsearchResponse> => {
    const response = await api.post(`/sourcing/deepsearch/${apolloId}`);
    return response.data;
  },

  // Get previously unlocked contacts by Apollo IDs
  getUnlockedContacts: async (
    apolloIds: string[],
  ): Promise<{ success: boolean; data: UnlockedContact[] }> => {
    const params = new URLSearchParams();
    params.set("apolloIds", apolloIds.join(","));
    const response = await api.get(
      `/sourcing/unlocks/batch?${params.toString()}`,
    );
    return response.data;
  },

  // Get daily credit usage
  getDailyCredits: async (): Promise<{
    success: boolean;
    data: CreditUsage;
  }> => {
    const response = await api.get("/sourcing/credits");
    return response.data;
  },

  // Import external profile
  importProfile: async (
    matchId: number,
    jobId: number,
    createApplication: boolean = false,
  ) => {
    const response = await api.post(`/sourcing/matches/${matchId}/import`, {
      jobId,
      createApplication,
    });
    return response.data;
  },
};
